<script lang="ts">
  import type {
    AidenRequest,
    AidenResponse,
    GetAttribute,
    IWebshopAPI,
    OptionsFor,
    SetAttribute,
  } from "./types"
  import advisorData from "./helpers/advisorData"
  import WithLoader from "./components/WithLoader.svelte"
  import { setupSentry } from "./setup/sentry"
  import { initializeAdvisor, resetAdvisor } from "./setup/init"
  import { storage } from "./setup/storage/storage"
  import { parseConfig } from "./setup/parseConfig"
  import ScreenChangeEmitter from "./components/embed/interop/ScreenChangeEmitter.svelte"
  import scrollScreenToTop from "./components/embed/interop/scrollScreenToTop"
  import { advisorInfoStore } from "./svelte-stores/advisorInfoStore"
  import { abTestStore } from "./ABTest"
  import { navigationContext } from "./svelte-stores/navigationStore"

  import {
    type InternalAnalyticsEvent,
    type SendInternalAnalytic,
  } from "../analytics/internal/types"

  import {
    IntegrationsAnalytics,
    type IntegrationsAnalyticsForComponents,
  } from "../analytics/integrations/IntegrationsAnalytics"
  import Page from "./components/page/Page.svelte"
  import { errorPage, toPage } from "./components/page/toPage"
  import PageFrame from "./components/embed/Frame.svelte"
  import {
    appAnalyticsStore,
    createAnalyticsEmitter,
  } from "../analytics/appAnalytics"

  export let WebshopAPI: IWebshopAPI
  export let searchParams = window.location.search
  export const config = parseConfig(searchParams)
  export let data: undefined | AidenResponse = undefined
  const { advisorId, isPreview, mode } = config

  let initError = false
  let error = false
  let hasPrevScreen = false
  let initializing = config.resetSessionOnStart ? true : false
  let submitting = false
  let loadingPrevScreen = false
  let updatingQuestion = false

  const integrationsAnalytics = IntegrationsAnalytics()

  const scrollTop = () => {
    // Wait for next repaint to make sure the height has been adjusted and can be correctly scrolled to top
    // Using request animation frame breaks scrolling in Firefox,
    // probably related to different browser implmentations of refreshing the DOM.
    const TIMEOUT = 16
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }, TIMEOUT)
  }

  const initErrorCallback = (_e: unknown) => {
    initError = true
    initializing = false
    scrollTop()
  }

  let hasNewStyling = true

  const initCallback = (response: AidenResponse | undefined) => {
    initializing = false
    if (!response) {
      initError = true
    }
    if (response && response.screen) {
      data = response
      storage.set(advisorId, data)
      hasNewStyling = !!data?.styleSheet
      document.documentElement.lang = data.locale
      scrollTop()
    }
  }

  setupSentry()
  // Initialize
  if (advisorId !== "") {
    initializing = true
    initializeAdvisor(
      WebshopAPI,
      advisorId,
      isPreview,
      config,
      initCallback,
      initErrorCallback
    )
  }

  const handleResponse = (resp: AidenResponse): AidenResponse | undefined => {
    if (!resp) return undefined

    if (data?.currentScreenId !== resp.currentScreenId) {
      scrollTop()
    }
    if (!!resp.screen) {
      data = resp
      storage.set(advisorId, data)
    }
    return resp
  }

  const handleAnalytics = (
    advisorId: string,
    resp: AidenResponse,
    req: AidenRequest
  ): AidenResponse => {
    integrationsAnalytics.screenChange(advisorId, resp, req)
    return resp
  }

  const submit = () => {
    if (!data) return

    submitting = true

    const req = {
      ...data,
      screenId: data.currentScreenId,
      clientInfo: { userAgent: navigator.userAgent },
    }

    WebshopAPI.submit(advisorId, isPreview, config, req)
      .then(handleResponse)
      .then((resp: any) => {
        if (resp && resp.name !== "AbortError") {
          submitting = false
        }
        return resp
      })
      .then((resp) => handleAnalytics(advisorId, resp, req))
      .catch(() => {
        submitting = false
      })
      .finally(() => {
        submitting = false
      })
  }

  const update = (screenId?: string | null) => {
    if (!data) return
    if (submitting) return
    if (initializing) return
    screenId ? (loadingPrevScreen = true) : (updatingQuestion = true)

    const req = {
      ...data,
      screenId: screenId || data.currentScreenId,
      clientInfo: { userAgent: navigator.userAgent },
    }

    WebshopAPI.update(advisorId, isPreview, config, req)
      .then(handleResponse)
      .then((response: any) => {
        // TS hacks: response is sometimes AbortError
        if (response && response.name !== "AbortError") {
          updatingQuestion = false
          loadingPrevScreen = false
        }
        return response
      })
      .catch(() => {
        updatingQuestion = false
        loadingPrevScreen = false
      })
  }

  const setAttribute: SetAttribute = (
    attributeInstanceId,
    value,
    persist = "update"
  ) => {
    if (!data) return
    //only allow updating attributes that belong to the current screen
    if (attributeInstanceId.startsWith(data.currentScreenId)) {
      data = advisorData.setAttribute(data, attributeInstanceId, value)
      storage.set(advisorId, data)
      if (persist === "submit") {
        submit()
      } else if (persist === "update") {
        update()
      }
    }
  }

  const getAttribute: GetAttribute = (attributeInstanceId: string) =>
    data ? advisorData.getAttribute(data, attributeInstanceId) : null

  const prev = () => data && update(data.prevScreenId)

  const next = () => {
    submit()
  }

  let isStartingOver = false

  const reset = () => {
    if (!data) return
    initializing = true
    integrationsAnalytics.startOver(advisorId, data)
    isStartingOver = true
    resetAdvisor(
      WebshopAPI,
      advisorId,
      isPreview,
      config,
      $abTestStore.activeVariant,
      data.sessionId,
      (resp) => {
        isStartingOver = false
        initCallback(resp)
      },
      (resp) => {
        isStartingOver = false
        initErrorCallback(resp)
      }
    )
  }

  let analytic: SendInternalAnalytic = (event, screenId) => {
    if (!data) return
    const e = {
      ...event,
      screenId: screenId || data.currentScreenId,
      sessionId: data.sessionId,
      clientInfo: { userAgent: navigator.userAgent },
    } as InternalAnalyticsEvent
    WebshopAPI.analytic(advisorId, isPreview, config, e)
  }

  $: {
    appAnalyticsStore.set(
      createAnalyticsEmitter(
        isPreview,
        { id: advisorId, name: data?.advisor?.name || "" },
        $abTestStore.activeVariant,
        data?.sessionId || "",
        data?.integrations || [],
        analytic,
        integrationsAnalytics
      )
    )

    // appAnalytics.setContext(
    //   { id: advisorId, name: data?.advisor?.name || "" },
    //   $abTestStore.activeVariant,
    //   data?.sessionId || "",
    //   data?.integrations || []
    // )
  }

  // assignment to variable is required for reactivity
  let screenId = data?.screen.id
  $: screenId = data?.screen.id
  let scrollContainer: Element | undefined

  $: screenId && scrollScreenToTop(scrollContainer)

  let progress: number = 0
  $: {
    if (data) {
      progress = advisorData.calculateProgress(
        data.screenReportGroups[0]?.screenReports || [],
        data.currentScreenId
      )
      hasPrevScreen = data.prevScreenId ? true : false
    }
  }

  const optionsFor: OptionsFor = (
    optionsForField: string,
    attributeInstanceId: string,
    selected: { field: string; value: string }[]
  ) => {
    // Overwrite the current attribute for fetching options
    // to make sure correct options are retrieved.
    // Otherwise each option level is just one value once a user
    // navigates back.
    // The API uses only the entities for determining options.
    const valueJustForOptions = selected.map((opt, index) => {
      return `${index}:${opt.value}`
    })
    const entities = data
      ? advisorData.updateForEntity(
          data.entities,
          attributeInstanceId,
          valueJustForOptions
        )
      : undefined
    return WebshopAPI.optionsFor(
      advisorId,
      data ? data.currentScreenId : "",
      isPreview,
      optionsForField,
      config,
      entities
    )
  }

  let customerAnalyticsForComponents: IntegrationsAnalyticsForComponents
  $: {
    const sessionId = data?.sessionId || ""
    const integrations = data?.integrations || []

    customerAnalyticsForComponents = {
      productClick: (product) =>
        integrationsAnalytics.productClick(
          advisorId,
          $advisorInfoStore.advisorName,
          sessionId,
          integrations,
          product
        ),
      customerInfoSubmitted: (info) =>
        integrationsAnalytics.customerInfoSubmitted(
          advisorId,
          $advisorInfoStore.advisorName,
          sessionId,
          isPreview,
          integrations,
          info
        ),
      infoPageCustomerInfoSubmitted: (info) =>
        integrationsAnalytics.infoPageCustomerInfoSubmitted(
          advisorId,
          $advisorInfoStore.advisorName,
          sessionId,
          isPreview,
          integrations,
          info
        ),
      noResultsCustomerInfoSubmitted: (info) =>
        integrationsAnalytics.noResultsCustomerInfoSubmitted(
          advisorId,
          $advisorInfoStore.advisorName,
          sessionId,
          isPreview,
          integrations,
          info
        ),
    }
  }

  // Sync data to storage
  $: data && storage.set(advisorId, data)
  $: advisorInfoStore.setInfo({
    advisorId: advisorId,
    advisorName: data?.advisor?.name || "",
    integrations: data?.integrations || [],
  })
  $: abTestStore.setVariant(data?.testVariant || null)

  $: {
    // Update navigation context
    navigationContext.set({
      refreshing: updatingQuestion,
      loadingNextScreen: submitting,
      startingOver: isStartingOver,
      loadingPreviousScreen: loadingPrevScreen,
      hasNextScreen: !!data?.nextScreenId,
      hasPreviousScreen: hasPrevScreen,
      initializing: initializing,
      progress,
      prev,
      next,
      reset,
    })
  }

  let showPoweredByAiden = false
  $: showPoweredByAiden =
    (data?.screen?.properties || {})["showPoweredByAiden"] === "true" || false

  let hasError = !advisorId || initError
  $: hasError = !advisorId || initError

  let page = data ? toPage(data.screen, config) : undefined
  $: page = hasError
    ? errorPage()
    : data
      ? toPage(data.screen, config)
      : undefined

  let isLoading = true
  $: isLoading = (!data && !page) || initializing
</script>

<main
  class="aiden-webshop-app"
  class:is-modal={config.mode === "modal" || config.mode === "sidebar"}
  class:has-inpage-advice-bg={page?.type === "advice-page" ||
    page?.type === "product-check-advice-page" ||
    page?.type === "empty-advice-page"}
  class:has-inpage-start-bg={page?.type === "start-page"}
  class:has-inpage-bg={config.mode === "inpage"}
  bind:this={scrollContainer}
>
  {#if data}
    <ScreenChangeEmitter {advisorId} screenId={data.screen.id} />
  {/if}

  {#if isLoading}
    <div class="loader" class:centered-loader={true}>
      <WithLoader isLoading={true} />
    </div>
  {:else}
    <PageFrame
      {progress}
      {page}
      hideProgress={isLoading ||
        page?.type === "error-page" ||
        data?.screen?.id === "intro" ||
        data?.screen?.id === "ADVICE" ||
        data?.screen?.id === null}
      hideStartOver={isLoading ||
        page?.type === "error-page" ||
        data?.screen?.id === "intro" ||
        data?.prevScreenId === null}
      embedMode={config.mode === "modal"
        ? "modal"
        : config.mode === "sidebar"
          ? "sidebar"
          : "fullpage"}
      {advisorId}
      {showPoweredByAiden}
      screenId={data?.screen?.id || ""}
    >
      {#if page}
        <Page
          {page}
          hidePrev={!hasPrevScreen}
          {setAttribute}
          {getAttribute}
          {optionsFor}
        />
      {:else}
        <div class="loader" class:centered-loader={config.mode !== "banner"}>
          <WithLoader isLoading={true} />
        </div>
      {/if}
    </PageFrame>
  {/if}
</main>

<style>
  main {
    min-height: 100%;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  }
  main.is-modal {
    background: rgba(0, 0, 0, 0.3);
  }

  .loader {
    color: #dcdcdc;
    align-items: center;
    display: flex;
    justify-content: start;
    padding: 1rem;
  }
  .centered-loader {
    justify-content: center;
  }
  .loader :global(svg) {
    height: 2rem;
    width: 2rem;
  }

  .has-inpage-bg {
    background: var(--aiden-inpage-background);
  }
  .has-inpage-advice-bg {
    background: var(--aiden-inpage-background-advice);
  }
  .has-inpage-start-bg {
    background: var(--aiden-inpage-background-start);
  }
</style>
